import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Book, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { formatDate } from '@xdocs/i18n';
import type { DocumentStatus } from '@xdocs/schemas';
import { Badge, Button, EmptyState, Mascot, SearchInput, Select, Skeleton, cn, useDebouncedValue, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { useDocHint } from './useDocHint.js';
import { useDocumentsList } from './useDocumentsList.js';
import { useDocumentsT } from './useDocumentsT.js';
import styles from './DocumentsPage.module.css';

const STATUS_TONE: Record<DocumentStatus, BadgeTone> = {
  draft: 'neutral',
  generating: 'info',
  ready: 'success',
  failed: 'error',
};

type StatusFilter = 'all' | 'ready' | 'generating' | 'draft';
const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'ready', 'generating', 'draft'];

type SortOption = 'updatedAt-desc' | 'createdAt-desc' | 'title-asc' | 'status-asc';
const SORT_OPTIONS: readonly SortOption[] = ['updatedAt-desc', 'createdAt-desc', 'title-asc', 'status-asc'];

const SKELETON_CARD_COUNT = 6;

/**
 * `/documents` (F1, FEATURES.md §"Documents list"): "grid/list of the
 * user's documents with custom previews (F32), status, difficulty badge,
 * language, persona" — a card grid, NOT `<DataTable>`'s table primitive
 * (E4 §8.3 reserves that for genuinely tabular resources; this route's own
 * spec text calls for a gallery). Server still does search/sort/pagination
 * (`useDocumentsList`) — only the STATUS filter chips are client-side: the
 * shared `listQueryInput` (`@xdocs/schemas`, owned outside this cluster) has
 * no status column yet, so "Ready/Generating/Drafts" narrow the current
 * page's already-fetched rows rather than a real server-side WHERE (open
 * issue — would need a schemas/server change to fix properly). Three
 * distinct empty kinds (E19/ux.md): first-use (mascot + CTA), no-results
 * (search OR status filter, each named), error (retry).
 */
export function DocumentsPage(): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useDocumentsT();
  const navigate = useNavigate();
  const { showDocHint, dismissDocHint } = useDocHint();
  const {
    rows,
    query,
    onQueryChange,
    onLoadMore,
    nextCursor,
    isLoading,
    isLoadingMore,
    isError,
    loadMoreError,
    onRetry,
  } = useDocumentsList();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [qDraft, setQDraft] = useState(query.q ?? '');
  const debouncedQ = useDebouncedValue(qDraft, 250);
  // Mirrors `<DataTable>`'s own resync guard (E6 §5.9): tracks the last `q`
  // WE pushed so an external reset (`clearFilters` below) can resync the
  // draft without the debounce round-trip echoing a stale value back over it.
  const lastPushedQ = useRef(query.q ?? '');

  useEffect(() => {
    if (debouncedQ === (query.q ?? '')) return;
    const { q: _currentQ, ...rest } = query;
    lastPushedQ.current = debouncedQ;
    onQueryChange(debouncedQ ? { ...rest, q: debouncedQ, page: 1 } : { ...rest, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    const externalQ = query.q ?? '';
    if (externalQ !== lastPushedQ.current) {
      lastPushedQ.current = externalQ;
      setQDraft(externalQ);
    }
  }, [query.q]);

  const sortValue = `${query.sort}-${query.dir}` as SortOption;
  const displayedRows = statusFilter === 'all' ? rows : rows.filter((doc) => doc.status === statusFilter);

  // The `⌘K` glyph must render as its own <kbd> chip (matches
  // AppShellLayout's search shortcut), so the sentence around it is split on
  // the raw "{shortcut}" token rather than interpolated to a plain string.
  const hintTemplate = t('documents:list.hint.text');
  const [hintBefore, hintAfter] = hintTemplate.split('{shortcut}');

  const hasActiveSearch = (query.q ?? '').length > 0;
  const isTrulyEmpty = !isLoading && !isError && rows.length === 0;
  const isFirstUseEmpty = isTrulyEmpty && !hasActiveSearch;
  const isSearchNoResults = isTrulyEmpty && hasActiveSearch;
  const isStatusNoResults = !isLoading && !isError && rows.length > 0 && displayedRows.length === 0;
  const isNoResults = isSearchNoResults || isStatusNoResults;

  function clearFilters(): void {
    setStatusFilter('all');
    setQDraft('');
    lastPushedQ.current = '';
    const { q: _q, ...rest } = query;
    onQueryChange({ ...rest, page: 1 });
  }

  function onSortChange(next: string): void {
    const [sort, dir] = next.split('-') as [string, 'asc' | 'desc'];
    onQueryChange({ ...query, sort, dir, page: 1 });
  }

  return (
    <div className={cn(styles['page'])}>
      {showDocHint && (
        <div className={cn(styles['hint'])}>
          {/* Mockup shows a 40px mascot; `Mascot` only ships 26/80/120/240
              raster sizes (packages/ui/src/assets/Mascot.tsx) — 26 is closest. */}
          <Mascot pose="idle" size={26} alt="" />
          <p className={cn(styles['hintText'])}>
            <b>{t('documents:list.hint.label')}</b> {hintBefore}
            {/* i18n-exempt: platform keybinding glyph, matches AppShellLayout's ⌘K chip */}
            <kbd className={cn(styles['hintKbd'])}>⌘K</kbd>
            {hintAfter}
          </p>
          <div className={cn(styles['hintActions'])}>
            <button type="button" className={cn(styles['hintDismissText'])} onClick={dismissDocHint}>
              {t('documents:list.hint.dismiss')}
            </button>
            <button
              type="button"
              className={cn(styles['hintClose'])}
              onClick={dismissDocHint}
              aria-label={t('documents:list.hint.close')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      <div className={cn(styles['header'])}>
        <div>
          <p className={cn(styles['eyebrow'])}>{t('documents:list.eyebrow')}</p>
          <h1 className={cn(styles['title'])}>{t('documents:list.title')}</h1>
        </div>
        <Button variant="primary" leadingIcon={<Plus size={17} aria-hidden="true" />} onClick={() => void navigate('/generate')}>
          {t('documents:list.newDoc')}
        </Button>
      </div>

      <div className={cn(styles['filterRow'])}>
        <div className={cn(styles['chips'])} role="group" aria-label={t('documents:list.filter.label')}>
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              aria-pressed={statusFilter === filter}
              className={cn(styles['chip'], statusFilter === filter && styles['chipActive'])}
              onClick={() => setStatusFilter(filter)}
            >
              {t(`documents:list.filter.${filter}`)}
            </button>
          ))}
        </div>
        <div className={cn(styles['toolsRow'])}>
          <SearchInput
            label={t('documents:list.searchLabel')}
            hideLabel
            placeholder={t('documents:list.searchPlaceholder')}
            value={qDraft}
            onChange={(event) => setQDraft(event.target.value)}
            onClear={() => setQDraft('')}
            clearLabel={tCommon('common:action.clearSearch')}
            containerClassName={cn(styles['searchField'])}
          />
          <Select
            label={t('documents:list.sort.label')}
            containerClassName={cn(styles['sortField'])}
            value={sortValue}
            onChange={(event) => onSortChange(event.target.value)}
            options={SORT_OPTIONS.map((opt) => ({ value: opt, label: t(`documents:list.sort.${opt}`) }))}
          />
        </div>
      </div>

      {isError && (
        <EmptyState kind="error" title={t('documents:list.error.title')} action={{ label: t('documents:list.error.retry'), onClick: onRetry }} />
      )}

      {!isError && isFirstUseEmpty && (
        <EmptyState
          kind="first-use"
          title={t('documents:list.empty.title')}
          description={t('documents:list.empty.description')}
          action={{ label: t('documents:list.empty.cta'), onClick: () => void navigate('/generate') }}
        />
      )}

      {!isError && isNoResults && (
        <EmptyState
          kind="no-results"
          title={
            isSearchNoResults
              ? t('documents:list.noResults.title', { query: query.q ?? '' })
              : t('documents:list.noResults.filtered', { status: t(`documents:list.filter.${statusFilter}`) })
          }
          action={{ label: t('documents:list.noResults.clear'), onClick: clearFilters }}
        />
      )}

      {isLoading && rows.length === 0 && (
        <div className={cn(styles['grid'])} aria-hidden="true">
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
            <div key={index} className={cn(styles['skeletonCard'])}>
              <Skeleton shape="tile" height="128px" />
              <div className={cn(styles['skeletonBody'])}>
                <Skeleton shape="text-line" width="70%" />
                <Skeleton shape="text-line" width="45%" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isError && !isTrulyEmpty && !isStatusNoResults && (
        <ul className={cn(styles['grid'])} aria-busy={isLoading || undefined}>
          {displayedRows.map((doc) => (
            <li key={doc.id}>
              <button type="button" className={cn(styles['card'])} onClick={() => void navigate(`/documents/${doc.id}`)}>
                <span className={cn(styles['cover'])}>
                  {doc.status === 'generating' && (
                    <span className={cn(styles['generatingDot'])} aria-hidden="true" />
                  )}
                  {/* mockup-18: cover icon is lucide "Book" (spine+cover path), not FileText */}
                  <Book size={26} aria-hidden="true" />
                </span>
                <span className={cn(styles['cardBody'])}>
                  <span className={cn(styles['cardTitle'])}>{doc.title}</span>
                  <span className={cn(styles['cardMeta'])}>
                    {t(`documents:kind.${doc.kind}`)} · {formatDate(locale, doc.updatedAt, { dateStyle: 'medium' })}
                  </span>
                  <span className={cn(styles['cardChips'])}>
                    <Badge tone={STATUS_TONE[doc.status]}>{t(`documents:status.${doc.status}`)}</Badge>
                    <Badge tone="neutral">{t(`documents:difficulty.${doc.difficultyLevel}`)}</Badge>
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isError && !isTrulyEmpty && !isStatusNoResults && nextCursor && (
        <div className={cn(styles['loadMoreRow'])}>
          {loadMoreError ? (
            <p role="alert" className={cn(styles['loadMoreError'])}>
              {loadMoreError}{' '}
              <button type="button" className={cn(styles['loadMoreRetry'])} onClick={onLoadMore}>
                {t('documents:list.error.retry')}
              </button>
            </p>
          ) : (
            <Button variant="secondary" state={isLoadingMore ? 'loading' : 'idle'} onClick={onLoadMore}>
              {t('documents:list.loadMore')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
